import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useI18n } from '../../i18n/i18n';
import { useGuest } from '../guest/guest-state';
import { MoonPicture } from '../kids/pictures';
import { ReadAloudButton, speak } from '../kids/read-aloud';
import {
  fitsToday,
  forToday,
  minutesLeft,
  secondsLeft,
  tick,
  timeIsUp,
  type ScreenTimeState,
} from './planner';

/**
 * Screen time (owner idea 2026-11-04, ADR 0017). A grown-up sets minutes per day; time counts
 * while a child page is open and visible. Places offer only what fits in the time left, and
 * "Time to rest" appears only at a natural break: a film playing, a challenge in progress or
 * unsaved workshop work holds it until they end. Today's use is kept on this browser only.
 */

const SAVE_EVERY_SECONDS = 15;

interface ScreenTimeValue {
  state: ScreenTimeState;
  limited: boolean;
  secondsLeft: number;
  fits: (seconds: number) => boolean;
  setLimit: (minutes: number | null) => void;
  /** A place says "do not interrupt now" (film playing, challenge in progress). */
  setHold: (id: string, active: boolean) => void;
  /** Time is used up but something is still being finished. */
  finishing: boolean;
  resting: boolean;
}

const ScreenTimeContext = createContext<ScreenTimeValue | null>(null);

const NO_LIMIT: ScreenTimeValue = {
  state: { limitMinutes: null, day: '', usedSeconds: 0 },
  limited: false,
  secondsLeft: Number.POSITIVE_INFINITY,
  fits: () => true,
  setLimit: () => undefined,
  setHold: () => undefined,
  finishing: false,
  resting: false,
};

/** Outside the provider (tests rendering a page alone) there is no limit. */
export function useScreenTime(): ScreenTimeValue {
  return useContext(ScreenTimeContext) ?? NO_LIMIT;
}

/** Holds "Time to rest" while `active` (a natural break has not been reached yet). */
export function useScreenTimeHold(active: boolean): void {
  const { setHold } = useScreenTime();
  const id = useId();
  useEffect(() => {
    setHold(id, active);
    return () => setHold(id, false);
  }, [id, active, setHold]);
}

export function ScreenTimeProvider({ children }: { children: ReactNode }) {
  const guest = useGuest();
  const { storedScreenTime, saveScreenTime, storage, catalogue } = guest;
  const [local, setLocal] = useState<ScreenTimeState | null>(null);
  const [holds, setHolds] = useState<ReadonlySet<string>>(() => new Set());
  const [today] = useState(() => new Date());
  const state = forToday(local ?? storedScreenTime, today);

  // The shortest film that could still be started decides when nothing fits any more.
  const shortest = useMemo(() => {
    if (catalogue.status !== 'ready') return null;
    const lengths = catalogue.data.items
      .filter((i) => i.availability.status === 'playable')
      .map((i) => i.durationSeconds);
    return lengths.length > 0 ? Math.min(...lengths) : null;
  }, [catalogue]);

  const upNow = timeIsUp(state, shortest);
  const held = holds.size > 0;
  const resting = upNow && !held;

  const latest = useRef({ state, resting, storage, stored: storedScreenTime, saveScreenTime });
  useEffect(() => {
    latest.current = { state, resting, storage, stored: storedScreenTime, saveScreenTime };
  });

  // Count one second at a time while a limit is set, the page is visible and not resting.
  useEffect(() => {
    let sinceSave = 0;
    const timer = window.setInterval(() => {
      const now = latest.current;
      if (now.storage === 'pending' || now.state.limitMinutes === null) return;
      if (now.resting || document.visibilityState === 'hidden') return;
      setLocal((prev) => tick(forToday(prev ?? now.stored, new Date())));
      sinceSave += 1;
      if (sinceSave >= SAVE_EVERY_SECONDS) {
        sinceSave = 0;
        now.saveScreenTime(tick(now.state));
      }
    }, 1000);
    const saveNow = () => {
      const now = latest.current;
      if (now.state.limitMinutes !== null) now.saveScreenTime(now.state);
    };
    window.addEventListener('pagehide', saveNow);
    document.addEventListener('visibilitychange', saveNow);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('pagehide', saveNow);
      document.removeEventListener('visibilitychange', saveNow);
    };
  }, []);

  const setLimit = useCallback((minutes: number | null) => {
    const next = { ...forToday(latest.current.state, new Date()), limitMinutes: minutes };
    setLocal(next);
    latest.current.saveScreenTime(next);
  }, []);

  const setHold = useCallback((id: string, active: boolean) => {
    setHolds((prev) => {
      if (prev.has(id) === active) return prev;
      const next = new Set(prev);
      if (active) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const value = useMemo<ScreenTimeValue>(
    () => ({
      state,
      limited: state.limitMinutes !== null,
      secondsLeft: secondsLeft(state),
      fits: (seconds) => fitsToday(state, seconds),
      setLimit,
      setHold,
      finishing: upNow && held,
      resting,
    }),
    [state, setLimit, setHold, upNow, held, resting],
  );

  return <ScreenTimeContext.Provider value={value}>{children}</ScreenTimeContext.Provider>;
}

/** The little clock in the header: a pie that empties (and minutes for bigger children). */
export function ScreenTimeChip() {
  const { t } = useI18n();
  const { ageMode } = useGuest();
  const { state, limited, finishing } = useScreenTime();
  if (!limited || state.limitMinutes === null) return null;
  const minutes = minutesLeft(state);
  const share = Math.max(0, Math.min(100, (secondsLeft(state) / (state.limitMinutes * 60)) * 100));
  return (
    <p
      className={minutes <= 2 || finishing ? 'time-chip is-low' : 'time-chip'}
      role="img"
      aria-label={t('time.chipLabel', { minutes })}
      data-testid="time-chip"
    >
      <svg viewBox="0 0 36 36" aria-hidden="true" focusable="false">
        <circle cx="18" cy="18" r="16" className="time-chip__ring" />
        <circle
          cx="18"
          cy="18"
          r="8"
          className="time-chip__fill"
          pathLength="100"
          strokeDasharray={`${share} 100`}
          transform="rotate(-90 18 18)"
        />
      </svg>
      {ageMode !== 'little' && (
        <span aria-hidden="true">{finishing ? t('time.finish') : t('time.chip', { minutes })}</span>
      )}
    </p>
  );
}

/** Full-screen goodbye once the time is used up and nothing is being finished. */
export function RestScreen({ onGrownUps }: { onGrownUps: () => void }) {
  const { t, locale } = useI18n();
  const { ageMode } = useGuest();
  const { resting } = useScreenTime();
  const text = t('rest.body');
  useEffect(() => {
    if (resting && ageMode === 'little') speak(`${t('rest.title')} ${text}`, locale, true);
  }, [resting, ageMode, locale, t, text]);
  if (!resting) return null;
  return (
    <section
      className="rest-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rest-title"
      data-testid="rest-screen"
    >
      <div className="rest-screen__card">
        <MoonPicture />
        <h1 id="rest-title">{t('rest.title')}</h1>
        <p>{text}</p>
        <ReadAloudButton
          text={`${t('rest.title')} ${text}`}
          slow
          className="read-aloud read-aloud--big"
        />
        <button type="button" className="grown-ups-link" onClick={onGrownUps}>
          {t('grown.button')}
        </button>
      </div>
    </section>
  );
}
